import { Button, Dialog, Group, MantineProvider, Modal, Progress, Text, UnstyledButton } from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import React, {
  createContext, useContext, useMemo
} from "react";
import { FaBackward } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { connect } from 'socket.io-client';
import InputField, { InputFieldTypes } from "../components/input_field";
import Details from "../components/themes/details";
import RolesSelect from "../components/roles_select";
import { GET_IMPORTING_FILES } from "../services/files";
import { useAuth } from "./auth.context";

interface ConfirmDialogProps{
  (
    props : {text:string, buttonText?:string},
    callback: (props: { confirmed: boolean }) => void
  ) : void
}
interface AppContextProps {
  openTheme: (th: any) => void;
  confirmDialog: ConfirmDialogProps;
  setColorScheme: (colorScheme : "dark" | "light") => void;
}

const socket = connect(process.env.REACT_APP_SERVER_URL)

const AppContext = createContext<AppContextProps>({} as AppContextProps);

export const AppProvider = ({ children }: { children: React.ReactNode }) => {
  const lsColorScheme = localStorage.getItem("@pol/colorScheme") as ("light" | "dark");
  
  const [showChildren, setShowChildren] = React.useState(true);
  const [openedConfirm, setOpenedConfirm] = React.useState(null);
  const [openThemeObj, setOpenTheme] = React.useState(null);
  const [importingFiles, setImportingFiles] = React.useState([]);
  const [colorScheme, setColorScheme] = React.useState<"dark" | "light">(lsColorScheme || "light");
  const [changedRoleId, setChangedRoleId] = React.useState(null);
  const [currentRole, setCurrentRole] = React.useState(null);
  
  const { role, onChangeRole } = useAuth();
  const navigate = useNavigate();

  const confirmDialog : ConfirmDialogProps = ({text, buttonText = "Confirmar"}, callback) => {
    setOpenedConfirm({text, buttonText, callback})
  }

  const loadImportingFiles = () => {
    GET_IMPORTING_FILES()
    .then(files => {
      setImportingFiles(files)
    })
    .catch(err => {
      showNotification({title: "Ops", message: err.message});
    })
  }

  const openTheme = (th) => {
    setOpenTheme(th);
  }

  const closeOpenTheme = () => {
    setOpenTheme(null);
    console.log({currentRole})
    currentRole && onChangeRole(currentRole._id, false);
  }

  React.useEffect(() => {
    if(openTheme) setCurrentRole(role);
    window.onpopstate = e => {
      if(openThemeObj){
        e.preventDefault();
        setOpenTheme(null);
        return false;
      }
    }
  }, [openThemeObj])

  React.useEffect(() => {
    localStorage.setItem("@pol/colorScheme", colorScheme);
  }, [colorScheme])
  
  React.useEffect(() => {
    if(role && role.company){
      loadImportingFiles() 
      socket.on(`files/importing/${role.company._id}`, loadImportingFiles)
      return () => { socket.off(`files/importing/${role.company._id}`) }
    }
  }, [role])

  const value = useMemo<AppContextProps>(() => {
    return { confirmDialog, setColorScheme, openTheme };
  }, [confirmDialog, setColorScheme]);

  return <MantineProvider
    withGlobalStyles
    withNormalizeCSS
    theme={{
      colorScheme,
      loader: 'dots',
      fontFamily: 'Montserrat, sans-serif',
      datesLocale: "pt-br",
      colors: {
        brand: ['#7AD1DD', '#5FCCDB', '#44CADC', '#028de0', '#028de0', '#028de0', '#028de0', '#028de0', '#028de0', '#028de0'],
        white: ['#FFFFFF', '#FFFFFF', '#FFFFFF', '#FFFFFF', '#FFFFFF', '#FFFFFF', '#FFFFFF', '#EFEFEF', '#FFFFFF', '#FFFFFF'],
      },
      headings: {
        fontWeight: "bold",
      },
      primaryColor: 'blue'
    }}
  >
    <AppContext.Provider value={value}>
      <>        
        <>
          <Dialog
              opened={Boolean(openedConfirm)}
              withCloseButton
              onClose={() => setOpenedConfirm(null)}
              size="lg"
              radius="md"
          >
          <Text size="sm" style={{ marginBottom: 10 }} weight={500}>
            {openedConfirm?.text}
          </Text>

          <Group align="flex-end">
              <Button onClick={() => {
                openedConfirm?.callback && openedConfirm?.callback({confirmed: true});
                setOpenedConfirm(null);
              }}>{openedConfirm?.buttonText}</Button>
          </Group>
          </Dialog>
        </>
    </>
    {children}
    <Dialog
      opened={importingFiles.length > 0}
    >
        {importingFiles.map(imp => <div>
          <div style={{textAlign: 'right'}}>
            <Text color="blue" size="sm">{imp.done}/{imp.total}</Text>
          </div>
          <Progress color="blue" value={imp.percent}  />
          <Text size="xs" style={{textAlign: 'right'}}>{{themes: "Importação de pautas"}[imp.type]} - {imp.origin.toUpperCase()}</Text>
        </div>)}
    </Dialog>

    <Modal
      opened={!!openThemeObj}
      onClose={closeOpenTheme}
      fullScreen
      styles={{
        header: {position: 'fixed', zIndex: 999, padding: '20px', left: 0, right: 0, top: 0, background: 'white'},
        body: {paddingTop: 40},
      }}
      title={<Group>
        <Button
          leftIcon={<FaBackward />}
          color="blue"
          variant="outline"
          onClick={closeOpenTheme}>
          Voltar
        </Button>
        <RolesSelect onChange={roleId => setChangedRoleId(roleId)} />
      </Group>}
    >
      {openThemeObj && showChildren && <Details id={openThemeObj.id} />}
    </Modal>
    </AppContext.Provider>
  </MantineProvider>
};

export const useApp = () => useContext(AppContext);
